import type { UIMessage } from 'ai'
import { toolPartDisplayName } from './components/llmChatToolPartHelpers'

const PRODUCT_RECOMMENDATION_TOOL_NAMES = new Set([
  'record_product_context',
  'suggest_product_recommendations',
  'collect_inferred_facts',
])

function isToolUIPart(part: UIMessage['parts'][number]): boolean {
  return part.type === 'dynamic-tool' || part.type.startsWith('tool-')
}

/**
 * Assistant bubble that only renders catalog recommendation / inferred-facts cards
 * (no assistant text). These HITL tool turns should not expose thumbs feedback.
 */
export function isProductRecommendationToolOnlyMessage(
  message: UIMessage
): boolean {
  if (message.role !== 'assistant' || message.parts.length === 0) {
    return false
  }

  return message.parts.every((part) => {
    if (!isToolUIPart(part)) {
      return false
    }
    return PRODUCT_RECOMMENDATION_TOOL_NAMES.has(toolPartDisplayName(part))
  })
}

/** Any bubble that renders a catalog recommendation / inferred-facts card. */
export function messageHasProductRecommendationTool(
  message: UIMessage
): boolean {
  if (message.role !== 'assistant') {
    return false
  }

  return message.parts.some((part) => {
    if (!isToolUIPart(part)) {
      return false
    }
    return PRODUCT_RECOMMENDATION_TOOL_NAMES.has(toolPartDisplayName(part))
  })
}

/**
 * Returns assistant message IDs that are the last in each contiguous assistant run
 * (the final assistant turn before the next user message or end of thread).
 */
export function getLastAssistantMessageIdsInRuns(
  messages: UIMessage[]
): Set<string> {
  const ids = new Set<string>()
  let lastAssistantIndex = -1

  const flushRun = () => {
    if (lastAssistantIndex < 0) {
      return
    }
    const message = messages[lastAssistantIndex]
    if (message?.role === 'assistant') {
      ids.add(message.id)
    }
    lastAssistantIndex = -1
  }

  for (let index = 0; index < messages.length; index += 1) {
    const message = messages[index]
    if (message.role === 'assistant') {
      lastAssistantIndex = index
    } else {
      flushRun()
    }
  }
  flushRun()
  return ids
}

/**
 * Last assistant message in each run, excluding catalog recommendation HITL turns.
 */
export function getFeedbackEligibleAssistantMessageIds(
  messages: UIMessage[]
): Set<string> {
  const lastInRuns = getLastAssistantMessageIdsInRuns(messages)
  const eligible = new Set<string>()

  for (const message of messages) {
    if (
      message.role === 'assistant' &&
      lastInRuns.has(message.id) &&
      !messageHasProductRecommendationTool(message)
    ) {
      eligible.add(message.id)
    }
  }

  return eligible
}
